import {
  makeStyles,
  tokens,
  Text,
  Spinner,
  Avatar,
  MessageBar,
  MessageBarBody,
  Table,
  TableHeader,
  TableHeaderCell,
  TableBody,
  TableRow,
  TableCell,
} from '@fluentui/react-components';
import { PeopleTeamRegular } from '@fluentui/react-icons';
import { useTranslation } from 'react-i18next';
import { useSalesTaxAdmins } from '../services/stateTaxClientsApi';
import ObligationSeedingPanel from '../components/management/ObligationSeedingPanel';
import UsageCard from '../components/management/UsageCard';
import ReadinessCard from '../components/management/ReadinessCard';
import SweepPreviewCard from '../components/management/SweepPreviewCard';
import { apiErrorMessage } from '../lib/apiErrorMessage';
import { pageShell } from '../components/layout/pageShell';

const useStyles = makeStyles({
  page: {
    ...pageShell,
  },
  header: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalXS,
    paddingBottom: tokens.spacingVerticalM,
    borderBottom: `1px solid ${tokens.colorNeutralStroke2}`,
  },
  title: {
    fontSize: tokens.fontSizeHero800,
    fontWeight: tokens.fontWeightSemibold,
    lineHeight: '1.1',
    margin: 0,
  },
  subtitle: {
    color: tokens.colorNeutralForeground3,
    fontSize: tokens.fontSizeBase300,
  },
  section: {
    display: 'flex',
    flexDirection: 'column',
    gap: tokens.spacingVerticalM,
  },
  sectionTitle: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
    fontSize: tokens.fontSizeBase500,
    fontWeight: tokens.fontWeightSemibold,
  },
  tableCard: {
    backgroundColor: tokens.colorNeutralBackground1,
    border: `1px solid ${tokens.colorNeutralStroke2}`,
    borderRadius: tokens.borderRadiusMedium,
    overflow: 'hidden',
  },
  tableHeader: {
    backgroundColor: tokens.colorNeutralBackground2,
  },
  nameCell: {
    display: 'flex',
    alignItems: 'center',
    gap: tokens.spacingHorizontalS,
  },
  emptyState: {
    padding: tokens.spacingVerticalXXL,
    textAlign: 'center',
    color: tokens.colorNeutralForeground3,
    backgroundColor: tokens.colorNeutralBackground2,
    borderRadius: tokens.borderRadiusMedium,
  },
  muted: {
    color: tokens.colorNeutralForeground3,
    fontStyle: 'italic',
  },
});

function initials(name: string): string {
  const parts = name.trim().split(/\s+/);
  const first = parts[0]?.[0] ?? '';
  const last = parts.length > 1 ? parts[parts.length - 1][0] : '';
  return (first + last).toUpperCase() || '?';
}

export default function ManagementPage() {
  const styles = useStyles();
  const { t } = useTranslation();
  const { data: admins, isLoading, isError, error } = useSalesTaxAdmins();

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <Text as="h1" className={styles.title}>
          {t('managementPage.title')}
        </Text>
        <span className={styles.subtitle}>{t('managementPage.subtitle')}</span>
      </div>

      <section className={styles.section}>
        <div className={styles.sectionTitle}>
          <PeopleTeamRegular />
          {t('managementPage.staffRoles.title')}
        </div>

        {isError && (
          <MessageBar intent="error">
            <MessageBarBody>
              {apiErrorMessage(error, t, 'managementPage.staffRoles.loadError')}
            </MessageBarBody>
          </MessageBar>
        )}

        {isLoading && <Spinner label={t('common.loading')} />}

        {!isLoading && !isError && (admins?.length ?? 0) === 0 && (
          <div className={styles.emptyState}>{t('managementPage.staffRoles.empty')}</div>
        )}

        {!isLoading && !isError && (admins?.length ?? 0) > 0 && (
          <div className={styles.tableCard}>
            <Table size="medium" aria-label={t('managementPage.staffRoles.title')}>
              <TableHeader className={styles.tableHeader}>
                <TableRow>
                  <TableHeaderCell>{t('managementPage.staffRoles.columns.name')}</TableHeaderCell>
                  <TableHeaderCell>{t('managementPage.staffRoles.columns.email')}</TableHeaderCell>
                  <TableHeaderCell>{t('managementPage.staffRoles.columns.role')}</TableHeaderCell>
                </TableRow>
              </TableHeader>
              <TableBody>
                {(admins ?? []).map((a) => (
                  <TableRow key={a.id}>
                    <TableCell>
                      <div className={styles.nameCell}>
                        <Avatar size={28} name={a.name} initials={initials(a.name)} color="colorful" />
                        {a.name}
                      </div>
                    </TableCell>
                    <TableCell>{a.email ?? <span className={styles.muted}>—</span>}</TableCell>
                    <TableCell>{t('managementPage.staffRoles.adminRole')}</TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          </div>
        )}
      </section>

      {/* STX-PROD-01 — weekly usage, admins only (renders null for anyone else). */}
      <UsageCard />

      {/* Grading loop P3: the automation-readiness board, admins only (renders null for anyone else). */}
      <ReadinessCard />

      {/* The nightly sweep's dry run as a button (admins only; writes nothing). Renders null for anyone else. */}
      <SweepPreviewCard />

      {/* Admin-only maintenance. Renders null for anyone not on the sales-tax admin
          roster, so a non-admin sees this page exactly as it was before. */}
      <ObligationSeedingPanel />
    </div>
  );
}
